// import lib(s)
import React, { useEffect, useMemo, useState, useRef, useCallback } from "react"
import { useDispatch, useSelector } from "react-redux"
import styles from "./MedianBeats.module.css"
import { useTheme } from "@emotion/react"

import ROUTES from "../../../route/routes"
import { useNavigate, useParams } from "react-router-dom"

import { useAxiosPrivate, useDocumentTitle } from "../../../hooks"
import { PAGE_TITLE } from "../../../constants/constants"

import Layout from "../../../layouts/common/Layout"
import PlaceholderText from "../../../components/common/SampleText"
import Loader from "../../../components/common/Loader"
import toast from "react-hot-toast"

import Tabs from "@mui/material/Tabs"
import Tab from "@mui/material/Tab"
import Box from "@mui/material/Box"
import Card from "@mui/material/Card"
import CardContent from "@mui/material/CardContent"
import Typography from "@mui/material/Typography"
import Button from "@mui/material/Button"
import Autocomplete from "@mui/material/Autocomplete"
import Divider from "@mui/material/Divider"
import TextField from "@mui/material/TextField"
import FormLabel from "@mui/material/FormLabel"
import FormControl from "@mui/material/FormControl"
import FormGroup from "@mui/material/FormGroup"
import FormControlLabel from "@mui/material/FormControlLabel"
import FormHelperText from "@mui/material/FormHelperText"
import Checkbox from "@mui/material/Checkbox"
import Switch from "@mui/material/Switch"
import Slider from "@mui/material/Slider"
import Link from "@mui/material/Link"
import List from "@mui/material/List"
import ListItem from "@mui/material/ListItem"
import ListItemButton from "@mui/material/ListItemButton"
import ListItemIcon from "@mui/material/ListItemIcon"
import ListItemText from "@mui/material/ListItemText"
import { Grid } from "@mui/material"

import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import { selectLead, addFiducialPoint, updateFiducialPointContext, updateGhostFiducialPoint, removeFiducialPoint, selectCurrentFiducialPoints, selectCurrentBeatType, selectCurrentMedianBeatWindow } from '../AnnotatorSlice'
import { FIDUCIAL_POINT_TYPES } from '../../../constants/annotator'
import FiducialPointContextMenu from '../components/FiducialPointContextMenu/FiducialPointContextMenu'

import ECGGrid from "../../../components/Grid"
import FullWidthECGPreview from "../../../components/FullWidthECGPreview"

import applyInversion from '../../../utils/applyInversion'
import { getMedianBeatFrequency, getMedianBeatRPeakSample, msToSample, sampleToMs } from '../../../utils/fiducialIntervals'
import { computeRmsWaveform } from '../../../utils/rmsWaveform'
import { computeMedianBeatWaveforms } from '../../../utils/medianBeatCompute'
import { resolveAnnotationFeatures } from '../../../utils/annotationFeatures'

// Sentinel key used to inject the RMS composite tile at the bottom of the
// per-lead median-beat grid.
const RMS_KEY = '__rms__'

const MedianBeats = () => {
	const dispatch = useDispatch()
	const navigate = useNavigate()
	const urlParams = useParams()
	const theme = useTheme()
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState(null)

	const boxes = useRef([]);
	const [scrollPosition, setScrollPosition] = useState(0);
	const [LeadGroup, setLeadGroup] = useState([]);

	// Annotator Data
	const AnnotatorData = useSelector((state) => state.Annotator)
	const { medianBeatFiducialPointAnnotation: medianBeatFiducialPointAnnotationEnabled } = resolveAnnotationFeatures(AnnotatorData.annotations)
	const { rmsView: rmsViewEnabled } = resolveAnnotationFeatures(AnnotatorData.annotations)
	const AnnotatorSettings = useSelector((state) => state.Annotator.settings)
	const currentFiducialPoints = useSelector(selectCurrentFiducialPoints)
	const currentBeatType = useSelector(selectCurrentBeatType)
	const currentWindow = useSelector(selectCurrentMedianBeatWindow)
	const ecg = AnnotatorData.filtered
	const totalLeads = AnnotatorData.leadCount
	const inverted = AnnotatorSettings.inverted
	const samples = useMemo(
		() => (inverted ? applyInversion(ecg.samples) : ecg.samples),
		[inverted, ecg.samples],
	)

	const [ThreeRowSamples, setThreeRowSamples] = useState({
		I: [],
		II: [],
		III: [],
		aVR: [],
		aVL: [],
		aVF: [],
		"V1-syn": [],
		"V2-syn": [],
		V1: [],
		V2: [],
		"V3-syn": [],
		V4: [],
		"V5-syn": [],
		"V6-syn": [],
	});
	const [ThreeRowSampleDuration, setThreeRowSampleDuration] = useState(2.5);
	const [Frequency, setFrequency] = useState(ecg.frequency);
	const [WindowCalc, setWindowCalc] = useState({windowWidthPerColumn: 0, samplePerPx: 0, samplesPerColumn: 0, totalSamples:0, samplesPerWindow: 0, newDuration: 0});

	const handleBoxScroll = useCallback((e, index) => {
		const scrollLeft = e.target.scrollLeft;
		setScrollPosition(scrollLeft);

		boxes.current.forEach((box, i) => {
		if (i !== index) {
			box.scrollLeft = scrollLeft;
		}
		});
	}, []);

	const handleFiducialPointRightClick = useCallback((e, sampleIndex) => {
		if (!medianBeatFiducialPointAnnotationEnabled) return
		const existingPoints = currentFiducialPoints
		const freq = getMedianBeatFrequency(AnnotatorData)
		const waveformLen = ThreeRowSamples?.II?.length || Object.values(ThreeRowSamples || {})[0]?.length || 0
		const rPeak = getMedianBeatRPeakSample(AnnotatorData, waveformLen)
		const nearbyPoint = existingPoints.find(fp => {
			const fpSample = msToSample(fp.offsetMs, freq, rPeak)
			return Math.abs(fpSample - sampleIndex) <= 5
		})
		dispatch(updateFiducialPointContext({
			show: true,
			data: {
				top: e.clientY,
				left: e.clientX,
				sampleIndex: sampleIndex,
				offsetMs: sampleToMs(sampleIndex, freq, rPeak),
				existingType: nearbyPoint?.type || null,
			}
		}))
	}, [dispatch, AnnotatorData, ThreeRowSamples, currentFiducialPoints, medianBeatFiducialPointAnnotationEnabled])

	const handleFiducialPointDrag = useCallback((type, sampleIndex) => {
		if (!medianBeatFiducialPointAnnotationEnabled) return
		const freq = getMedianBeatFrequency(AnnotatorData)
		const waveformLen = ThreeRowSamples?.II?.length || Object.values(ThreeRowSamples || {})[0]?.length || 0
		const rPeak = getMedianBeatRPeakSample(AnnotatorData, waveformLen)
		dispatch(addFiducialPoint({ type, offsetMs: sampleToMs(sampleIndex, freq, rPeak) }))
	}, [dispatch, AnnotatorData, ThreeRowSamples, medianBeatFiducialPointAnnotationEnabled])


	const HeightPerStrip = 200
	const AllLeadWidth = (10*100) * AnnotatorData.settings.scale

	// Translate stored ms-relative points into sample-space for the preview.
	const renderedFiducialPoints = useMemo(() => {
		const freq = getMedianBeatFrequency(AnnotatorData)
		const waveformLen = ThreeRowSamples?.II?.length || Object.values(ThreeRowSamples || {})[0]?.length || 0
		const rPeak = getMedianBeatRPeakSample(AnnotatorData, waveformLen)
		return currentFiducialPoints.map(fp => ({
			type: fp.type,
			sample: msToSample(fp.offsetMs, freq, rPeak),
		}))
	}, [AnnotatorData, ThreeRowSamples, currentFiducialPoints])

	// RMS median-beat trace: sqrt(mean(lead_k[i]^2)) across all leads.
	// computeRmsWaveform tolerates empty/typed-array/plain-array leads.
	const rmsWaveform = useMemo(() => computeRmsWaveform(ThreeRowSamples), [ThreeRowSamples])

	useEffect(() => {
		const baseLeadKeys = Object.keys(samples)
		// Append the RMS composite tile at the end so it lands at the bottom
		// of the last column.
		const leadKeys = rmsViewEnabled ? [...baseLeadKeys, RMS_KEY] : baseLeadKeys
		const leadGroups = leadKeys.reduce((resultArray, item, index) => {
			const chunkIndex = Math.floor(index/3)
			if(!resultArray[chunkIndex]) {
				resultArray[chunkIndex] = []
			}
			resultArray[chunkIndex].push(item)
			return resultArray
		}, [])
		setLeadGroup(leadGroups)

		const windowWidthPerColumn = 1000/leadGroups.length
		// ecg.frequency number of samples in 100px
		const samplePerPx = ecg.frequency/100
		const samplesPerColumn = samplePerPx*windowWidthPerColumn
		const totalSamples  = ecg.frequency*10
		const samplesPerWindow = (leadGroups.length) * samplesPerColumn
		let newDuration
		// let newDuration = (totalSamples - samplesPerWindow + samplesPerColumn) / ecg.frequency

		// -- console.log("Total samples: ", totalSamples);
		// -- console.log("Window Size: ", samplesPerWindow);
		// -- console.log("New Duration: ", newDuration);

		// Leads of leadGroups[0] will from 0 index to samplesPerColumn*leadGroups.length
		// Leads of leadGroups[1] will from samplesPerColumn to samplesPerColumn*(leadGroups.length - 1)

		if (totalLeads == 12) {
			const computed = computeMedianBeatWaveforms(AnnotatorData, { requireComputable: false })
			if (computed) {
				setFrequency(computed.renderRate)
				setThreeRowSamples(computed.waveforms)
			} else {
				setThreeRowSamples({})
			}
			newDuration=2.5;
		} else {
			const computed = computeMedianBeatWaveforms(AnnotatorData, { slug: currentBeatType, requireComputable: false })
			const output = computed?.waveforms || {}
			const renderRate = computed?.renderRate || AnnotatorData.filtered.frequency
			setThreeRowSamples({ ...output })
			newDuration=10;
			setFrequency(renderRate)
			// Prune any fiducial points that no longer fall within the recomputed window.
			// Gate on the window-loaded marker: on initial load the settings briefly hold
			// the slice defaults (600/600) before RightPanel dispatches the saved values,
			// and pruning during that interval would delete any point outside ±600ms even
			// if the saved window would have kept it. Once the marker matches the current
			// recording, the window is authoritative and pruning is safe.
			if (medianBeatFiducialPointAnnotationEnabled && AnnotatorData.medianBeatWindowLoadedForRecordingId === AnnotatorData.currentRecordingId) {
				const leftMs = currentWindow.leftMs
				const rightMs = currentWindow.rightMs
				const existingPoints = currentFiducialPoints
				existingPoints.forEach(fp => {
					if (fp.offsetMs < -leftMs || fp.offsetMs > rightMs) {
						dispatch(removeFiducialPoint(fp.type))
					}
				})
			}
		}

		setWindowCalc({windowWidthPerColumn, samplePerPx, samplesPerColumn, totalSamples, samplesPerWindow, newDuration})
		setThreeRowSampleDuration(newDuration)
		setLoading(false)
	}, [samples, AnnotatorSettings, AnnotatorData.annotations.beats, currentBeatType, medianBeatFiducialPointAnnotationEnabled, rmsViewEnabled])
	


	return (
		<>
		{loading ? (
			<Loader />
		) : error ? (
			<PlaceholderText>Error</PlaceholderText>
		) : (
			<React.Fragment>
			<div className={styles.Container} style={{height: `${(HeightPerStrip * 3) * AnnotatorData.settings.scale}px`, width: `${AllLeadWidth}px`}}>
				{LeadGroup.map((leadGroup, indexL) => (
					<div
						key={leadGroup}
						style={{ width: `${100 / LeadGroup.length}%`, overflowX: "hidden"}}
						className={styles.box}
						ref={(el) => (boxes.current[indexL] = el)}
						onScroll={(e) => handleBoxScroll(e, indexL)}
					>
						<div style={{ height: `${HeightPerStrip * 3 * AnnotatorData.settings.scale}px`, width: `${AllLeadWidth}px`, position: "relative" }}>
							{leadGroup.map((lead, index) => {
								const isRms = lead === RMS_KEY
								const tileTitle = isRms ? "RMS" : lead
								const tileSample = isRms ? rmsWaveform : ThreeRowSamples[lead]
								return (
								<div style={{height:`${HeightPerStrip * AnnotatorData.settings.scale}px`, "--ECGScale": AnnotatorData.settings.scale, position: "relative"}} key={index}>
									<FullWidthECGPreview
										key={index}
										ecgTitle={tileTitle}
										enableBeats={false}
										scale={AnnotatorData.settings.scale}
										sample={tileSample}
										seconds={ThreeRowSampleDuration || 2.5}
										heightFactor={2}
										mvSwing={ecg.gain * 2.5}
										onClick={isRms ? undefined : ()=>{dispatch(selectLead(lead))}}
										onRightClick={medianBeatFiducialPointAnnotationEnabled ? handleFiducialPointRightClick : undefined}
										onFiducialPointDrag={medianBeatFiducialPointAnnotationEnabled ? handleFiducialPointDrag : undefined}
										style={{cursor: "pointer"}}
										frequency={Frequency}
										offsetGrid={!(indexL%2 === 0) && WindowCalc.newDuration == 2.5}
										fiducialPoints={renderedFiducialPoints}
										ghostFiducialPoint={medianBeatFiducialPointAnnotationEnabled && AnnotatorData.ghostFiducialPoint?.show ? { sampleIndex: AnnotatorData.ghostFiducialPoint.sampleIndex } : null}
										fiducialPointLabel={FIDUCIAL_POINT_TYPES}
									/>
								</div>
								)
							})}
						</div>
					</div>
				))}
			</div>
			{/* {ThreeRowVerticalSamples.map((sample, index) => (
				<div key={index} className={styles.fullLead}>
					<div style={{ height: `199px`, width: `${AllLeadWidth}px`, position: "relative" }}>
						<span style={{ position: "fixed", padding: "5px", fontWeight: "900", borderRadius: "4px", backgroundColor: "#ffffff66", color: "#000" }}>{AnnotatorData.selectedLead || "II"}</span>
						<FullWidthECGPreview
							enableBeats={true}
							sample={sample}
							beats={AnnotatorData.annotations.beats}
							seconds={10 || 30}
							heightFactor={2}
							mvSwing={ecg.gain * 2.5}
							frequency={ecg.frequency}
							// renderTimeScaleFactor = {2}	
						/>
					</div>
				</div>
			))} */}
			{samples["II"] && (
				<div className={styles.fullLead}>
					<div style={{ height: `${170 * AnnotatorData.settings.scale}px`, width: `${AllLeadWidth}px`, position: "relative" }}>
						{/* <span style={{ position: "fixed", padding: "5px", fontWeight: "900", borderRadius: "4px", backgroundColor: "#ffffff66", color: "#000" }}>{AnnotatorData.selectedLead || "II"}</span> */}
						<FullWidthECGPreview
							enableBeats={true}
							ecgTitle = {AnnotatorData.selectedLead || "II"}
							sample={samples[AnnotatorData.selectedLead || "II"]}
							scale = {AnnotatorData.settings.scale}
							beats={AnnotatorData.annotations.beats}
							beatLabel = {AnnotatorData.annotations.labels.beats}
							seconds={10}
							heightFactor={1.7}
							mvSwing={ecg.gain * 2.5}
							frequency={ecg.frequency}
							renderTimeScaleFactor = {1}
						/>
					</div>
				</div>
			)}
			{medianBeatFiducialPointAnnotationEnabled && <FiducialPointContextMenu />}
			</React.Fragment>
		)}
	</>
)
}

export default MedianBeats
